import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import SapaHeader from "../../ui/Header/Header";
import {
  createManagementResourceRequest,
  deleteManagementResourceRequest,
  getManagementResourceStatsRequest,
  listManagementResourceRequest,
  updateManagementResourceRequest,
} from "../../api/management";
import SpPagination from "../../components/SpPagination/SpPagination";
import DataTable from "../../ui/DataTable/DataTable";
import Filterbox from "../../ui/FilterBox/Filterbox";
import ProgressLoader from "../../components/Loader/ProgressLoader";

const RESOURCE_GROUPS = [
  {
    key: "rates",
    label: "Rates",
    resources: [
      { key: "gift-card-rates", label: "Gift Card Rates" },
      { key: "gift-card-services", label: "Gift Card Services" },
      { key: "gift-card-units", label: "Gift Card Units" },
      { key: "gift-card-subcategories", label: "Gift Card Subcategories" },
      { key: "crypto-currencies", label: "Crypto Rates" },
      { key: "crypto-networks", label: "Crypto Networks" },
    ],
  },
  {
    key: "business",
    label: "Business Config",
    resources: [
      { key: "app-configs", label: "App Configs" },
      { key: "transaction-limits", label: "Transaction Limits" },
      { key: "user-charges", label: "User Charges" },
      { key: "provider-commissions", label: "Provider Commissions" },
      { key: "std-commissions", label: "Standard Commissions" },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    resources: [
      { key: "withdrawal-transactions", label: "Withdrawals" },
      { key: "gift-card-transactions", label: "Gift Card Transactions" },
      { key: "gift-card-batches", label: "Gift Card Batches" },
      { key: "gift-card-codes", label: "Gift Card Codes" },
      { key: "kyc-reviews", label: "KYC Reviews" },
      { key: "api-logs", label: "API Logs", readonly: true },
    ],
  },
  {
    key: "messaging",
    label: "Messaging",
    resources: [
      { key: "notifications", label: "Notifications" },
      { key: "marketing-push-notifications", label: "Marketing Push" },
    ],
  },
];

const FIELD_LABELS = {
  service_id: "Service ID",
  country_id: "Country ID",
  network_id: "Network ID",
  user_id: "User",
  provider_id: "Provider",
  channel_id: "Channel",
  base_currency: "Base Currency",
  target_currency: "Target Currency",
  conversion_rate: "Conversion Rate",
  display_rate: "Display Rate",
  selling_rate: "Selling Rate",
  buying_rate: "Buying Rate",
  vendor_selling_rate: "Vendor Selling Rate",
  vendor_buying_rate: "Vendor Buying Rate",
  minimum_allowed_amount: "Minimum Allowed Amount",
  maximum_allowed_amount: "Maximum Allowed Amount",
  sort_code: "Sort Code",
  sort_order: "Sort Order",
  is_active: "Active",
  is_recommended: "Recommended",
  is_flat_rate: "Flat Rate",
  has_receipt: "Receipt Required",
  supports_pre_order: "Supports Pre-order",
  denomination_type: "Denomination Type",
  discount_percentage: "Discount Percentage",
  recipient_currency: "Recipient Currency",
  sender_currency: "Sender Currency",
  sender_denominations_map: "Sender Denominations",
  recipient_denominations_map: "Recipient Denominations",
  recipient_to_sender_denominations_map: "Recipient To Sender Denominations",
  redeem_instruction: "Redeem Instruction",
  deposits_enabled: "Deposits Enabled",
  withdraws_enabled: "Withdrawals Enabled",
};

const RESOURCE_FIELDS = {
  "app-configs": ["key", "value", "type", "group", "description", "is_active"],
  "crypto-currencies": [
    "code",
    "display_name",
    "operation",
    "service_id",
    "network_id",
    "reference_currency",
    "settlement_currency",
    "minimum_allowed_amount",
    "maximum_allowed_amount",
    "conversion_rate",
    "rate_source",
    "is_flat_rate",
    "sort_order",
    "is_recommended",
    "status",
  ],
  "crypto-networks": ["name", "code", "deposits_enabled", "withdraws_enabled", "sort_order", "status"],
  "gift-card-rates": [
    "service_id",
    "country_id",
    "base_currency",
    "target_currency",
    "display_rate",
    "selling_rate",
    "buying_rate",
    "vendor_selling_rate",
    "vendor_buying_rate",
    "type",
    "value",
    "is_physical_card",
    "description",
    "sort_code",
    "status",
  ],
  "gift-card-transactions": ["transaction_reference", "req", "res", "status"],
  "gift-card-batches": ["brand", "unit_amount", "quantity", "base_currency", "target_currency", "rate", "sms_fee", "transaction_fee", "request_code", "status"],
  "gift-card-codes": ["batch_id", "ecode", "status", "transaction_reference"],
  "gift-card-services": [
    "code",
    "name",
    "operation",
    "country_id",
    "service_id",
    "conversion_rate",
    "sms_fee",
    "recipient_currency",
    "sender_currency",
    "denomination_type",
    "discount_percentage",
    "sender_denominations_map",
    "recipient_denominations_map",
    "recipient_to_sender_denominations_map",
    "is_recommended",
    "is_physical_card",
    "has_receipt",
    "supports_pre_order",
    "redeem_instruction",
    "metadata",
    "sort_code",
    "status",
  ],
  "gift-card-subcategories": ["type", "name", "value", "slug", "sort_code", "service_id", "country_id", "description", "status"],
  "gift-card-units": ["amount", "display_name", "country_id", "service_id", "status"],
  "kyc-reviews": ["user_id", "kyc_verified_data_id", "target_tier", "status", "review_remark"],
  "marketing-push-notifications": ["title", "body", "topic", "data", "is_enabled", "scheduled_at", "status"],
  notifications: ["user_id", "title", "message", "type", "is_read", "is_collapsible"],
  "provider-commissions": ["provider_id", "service_id", "channel_id", "value", "cap", "is_flat"],
  "std-commissions": ["service_type", "level", "amount", "percentage", "status"],
  "transaction-limits": ["user_id", "service_id", "tier", "type", "period", "amount", "count", "status"],
  "user-charges": ["user_id", "service_id", "provider_id", "channel_id", "amount", "percentage", "status"],
  "withdrawal-transactions": ["transaction_id", "status", "note", "retry_at"],
};

const BOOLEAN_FIELDS = ["is_active", "is_recommended", "is_flat_rate", "deposits_enabled", "withdraws_enabled", "is_enabled", "is_read", "is_collapsible", "is_physical_card", "has_receipt", "supports_pre_order"];
const NUMBER_FIELDS = ["amount", "percentage", "service_id", "country_id", "network_id", "user_id", "provider_id", "channel_id", "transaction_id", "target_tier", "sort_code", "sort_order", "display_rate", "selling_rate", "buying_rate", "vendor_selling_rate", "vendor_buying_rate", "conversion_rate", "minimum_allowed_amount", "maximum_allowed_amount", "count", "kyc_verified_data_id", "value", "cap", "batch_id", "unit_amount", "quantity", "rate", "sms_fee", "transaction_fee", "discount_percentage"];
const JSON_FIELDS = ["value", "data", "req", "res", "sender_denominations_map", "recipient_denominations_map", "recipient_to_sender_denominations_map", "redeem_instruction", "metadata"];
const STATUS_OPTIONS = ["active", "inactive", "pending", "suspended", "archived", "draft", "queued", "completed", "failed", "available", "used", "upcoming"];
const OPERATION_OPTIONS = ["BUY", "SELL"];
const TYPE_OPTIONS = ["fixed", "multiple", "range", "multi_range", "daily", "weekly", "monthly", "transaction"];
const DENOMINATION_TYPE_OPTIONS = ["FIXED", "RANGE", "FIXED-RANGE", "MULTI-FIXED-RANGE", "MULTI-RANGE"];

const statusColor = (value) => {
  const status = String(value || "").toLowerCase();
  if (["success", "successful", "active", "available", "completed", "approved", "verified"].includes(status)) return "green";
  if (["pending", "initiated", "processing", "queued", "review", "draft"].includes(status)) return "gold";
  if (["failed", "declined", "blocked", "suspended", "inactive", "canceled", "cancelled", "archived", "used"].includes(status)) return "red";
  if (["refund", "refunded", "reversed"].includes(status)) return "blue";
  return "default";
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const labelFor = (field) =>
  FIELD_LABELS[field] || field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const firstValue = (record, keys) => keys.map((key) => record?.[key]).find((value) => value !== undefined && value !== null && value !== "");

const relatedLabel = (record, field) => {
  const relationKey = field.replace(/_id$/, "");
  const relation = record?.[relationKey];

  if (field === "user_id") {
    const profile = relation?.user_profile || record?.user?.user_profile;
    const fullName = profile?.full_name || `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();
    return fullName || relation?.email || record?.user?.email;
  }

  if (field === "transaction_id") {
    return record?.transaction?.transaction_reference || record?.transaction?.id;
  }

  if (field === "kyc_verified_data_id") {
    return record?.kyc_verified_data?.identity_number || record?.kyc_verified_data?.id;
  }

  if (relation && typeof relation === "object") {
    return relation.name || relation.display_name || relation.title || relation.code || relation.email || relation.id;
  }

  return undefined;
};

const displayRecordField = (record, field) => relatedLabel(record, field) || formatValue(record?.[field]);

const resolveInitialGroup = (resource) =>
  RESOURCE_GROUPS.find((group) => group.resources.some((item) => item.key === resource))?.key || RESOURCE_GROUPS[0].key;

const Management = ({ initialGroup, initialResource, initialSearchTerm = "", pageTitle = "Management" }) => {
  const [activeGroup, setActiveGroup] = useState(initialGroup || resolveInitialGroup(initialResource));
  const [activeResource, setActiveResource] = useState(initialResource || RESOURCE_GROUPS[0].resources[0].key);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 15, total: 0 });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusToggleId, setStatusToggleId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const isDedicatedPage = Boolean(initialResource);

  const currentGroup = useMemo(
    () => RESOURCE_GROUPS.find((group) => group.key === activeGroup) || RESOURCE_GROUPS[0],
    [activeGroup]
  );

  const currentResource = useMemo(
    () => currentGroup.resources.find((resource) => resource.key === activeResource) || currentGroup.resources[0],
    [activeResource, currentGroup]
  );

  const fields = useMemo(() => RESOURCE_FIELDS[activeResource] || [], [activeResource]);

  useEffect(() => {
    if (initialResource) {
      setActiveResource(initialResource);
      setActiveGroup(initialGroup || resolveInitialGroup(initialResource));
      setSearchTerm(initialSearchTerm);
    }
  }, [initialGroup, initialResource, initialSearchTerm]);

  const fetchRecords = useCallback(
    async (nextPage = 1, nextSearchTerm = searchTerm) => {
      setLoading(true);
      try {
        const [listResponse, statsResponse] = await Promise.all([
          listManagementResourceRequest(activeResource, { nextPage, search_term: nextSearchTerm }),
          getManagementResourceStatsRequest(activeResource),
        ]);

        const payload = listResponse?.data?.data || {};
        setRecords(payload?.data || []);
        setPagination(payload);
        setStats(statsResponse?.data?.data || null);
      } catch (error) {
        message.error(error?.response?.data?.message || "Unable to load management data.");
      } finally {
        setLoading(false);
      }
    },
    [activeResource, searchTerm]
  );

  useEffect(() => {
    fetchRecords(1);
  }, [fetchRecords]);

  useEffect(() => {
    const firstResource = currentGroup.resources[0]?.key;
    if (firstResource && !currentGroup.resources.some((resource) => resource.key === activeResource)) {
      setActiveResource(firstResource);
    }
  }, [activeGroup, activeResource, currentGroup]);

  const openModal = useCallback((record = null) => {
    setEditingRecord(record);
    form.resetFields();

    if (record) {
      const values = {};
      fields.forEach((field) => {
        const value = record[field];
        values[field] = JSON_FIELDS.includes(field) && value ? JSON.stringify(value, null, 2) : value;
      });
      form.setFieldsValue(values);
    }

    setIsModalOpen(true);
  }, [fields, form]);

  const submitForm = async () => {
    try {
      const values = await form.validateFields();
      const payload = {};

      Object.entries(values).forEach(([key, value]) => {
        if (value === undefined || value === "") return;
        payload[key] = JSON_FIELDS.includes(key) && typeof value === "string" ? JSON.parse(value) : value;
      });

      setActionLoading(true);
      try {
        if (editingRecord) {
          await updateManagementResourceRequest(activeResource, editingRecord.id, payload);
          message.success("Record updated.");
        } else {
          await createManagementResourceRequest(activeResource, payload);
          message.success("Record created.");
        }

        setIsModalOpen(false);
        fetchRecords(pagination?.current_page || 1);
      } finally {
        setActionLoading(false);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to save record.");
    }
  };

  const deleteRecord = useCallback(async (record) => {
    setActionLoading(true);
    try {
      await deleteManagementResourceRequest(activeResource, record.id);
      message.success("Record deleted.");
      fetchRecords(pagination?.current_page || 1);
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to delete record.");
    } finally {
      setActionLoading(false);
    }
  }, [activeResource, fetchRecords, pagination?.current_page]);

  const toggleGiftCardServiceStatus = useCallback(async (record, checked) => {
    setActionLoading(true);
    setStatusToggleId(record.id);
    try {
      await updateManagementResourceRequest("gift-card-services", record.id, {
        status: checked ? "active" : "inactive",
      });
      message.success(`Gift card service ${checked ? "activated" : "deactivated"}.`);
      fetchRecords(pagination?.current_page || 1);
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to update service status.");
    } finally {
      setStatusToggleId(null);
      setActionLoading(false);
    }
  }, [fetchRecords, pagination?.current_page]);

  const columns = useMemo(() => {
    const actionColumn = currentResource.readonly ? null : {
      title: "Actions",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button aria-label="Edit record" icon={<EditOutlined />} onClick={() => openModal(record)} />
          <Button
            danger
            aria-label="Delete record"
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "Delete record?",
                content: "This action cannot be undone.",
                okText: "Delete",
                okButtonProps: { danger: true },
                onOk: () => deleteRecord(record),
              });
            }}
          />
        </Space>
      ),
    };

    if (activeResource === "gift-card-services") {
      const giftCardServiceColumns = [
        {
          title: "Service",
          key: "service",
          width: 240,
          render: (_, record) => (
            <div className="management-table__primary">
              <strong>{firstValue(record, ["name", "display_name", "code", "id"])}</strong>
              <span>{record?.code || relatedLabel(record, "service_id") || "N/A"}</span>
            </div>
          ),
        },
        {
          title: "Country",
          key: "country",
          width: 160,
          render: (_, record) => relatedLabel(record, "country_id") || "N/A",
        },
        {
          title: "Operation",
          dataIndex: "operation",
          key: "operation",
          width: 120,
        },
        {
          title: "Conversion Rate",
          dataIndex: "conversion_rate",
          key: "conversion_rate",
          width: 160,
          render: (value) => Number(value || 0).toLocaleString("en-NG", { maximumFractionDigits: 4 }),
        },
        {
          title: "SMS Fee",
          dataIndex: "sms_fee",
          key: "sms_fee",
          width: 120,
          render: (value) => Number(value || 0).toLocaleString("en-NG", { maximumFractionDigits: 4 }),
        },
        {
          title: "Flags",
          key: "flags",
          width: 220,
          render: (_, record) => (
            <Space size={[6, 6]} wrap>
              {record?.is_recommended ? <Tag color="blue">RECOMMENDED</Tag> : null}
              {record?.is_physical_card ? <Tag color="gold">PHYSICAL</Tag> : null}
              {record?.supports_pre_order ? <Tag color="green">PRE-ORDER</Tag> : null}
            </Space>
          ),
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          width: 180,
          render: (value, record) => (
            <Space>
              <Tag color={statusColor(value)}>{String(value || "inactive").toUpperCase()}</Tag>
              {!currentResource.readonly && (
                <Switch
                  checked={String(value || "").toLowerCase() === "active"}
                  checkedChildren="On"
                  disabled={Boolean(statusToggleId)}
                  loading={statusToggleId === record.id}
                  unCheckedChildren="Off"
                  onChange={(checked) => toggleGiftCardServiceStatus(record, checked)}
                />
              )}
            </Space>
          ),
        },
        {
          title: "Updated",
          dataIndex: "updated_at",
          key: "updated_at",
          width: 180,
          render: (value) => (value ? new Date(value).toLocaleString() : "N/A"),
        },
      ];

      return actionColumn ? [...giftCardServiceColumns, actionColumn] : giftCardServiceColumns;
    }

    if (activeResource === "app-configs") {
      const appConfigColumns = [
        {
          title: "Key",
          dataIndex: "key",
          key: "key",
          width: 260,
          render: (value) => <strong className="management-table__config-key">{formatValue(value)}</strong>,
        },
        {
          title: "Type",
          dataIndex: "type",
          key: "type",
          width: 140,
          render: (value) => formatValue(value),
        },
        {
          title: "Group",
          dataIndex: "group",
          key: "group",
          width: 160,
          render: (value) => formatValue(value),
        },
        {
          title: "Description",
          dataIndex: "description",
          key: "description",
          width: 360,
          render: (value) => <span className="management-table__wrap">{formatValue(value)}</span>,
        },
        {
          title: "Active",
          dataIndex: "is_active",
          key: "is_active",
          width: 120,
          render: (value) => (
            <Tag color={value ? "green" : "red"}>{value ? "ACTIVE" : "INACTIVE"}</Tag>
          ),
        },
        {
          title: "Updated",
          dataIndex: "updated_at",
          key: "updated_at",
          width: 180,
          render: (value) => (value ? new Date(value).toLocaleString() : "N/A"),
        },
      ];

      return actionColumn ? [...appConfigColumns, actionColumn] : appConfigColumns;
    }

    const baseColumns = [
      {
        title: "Name",
        key: "name",
        width: 220,
        render: (_, record) =>
          firstValue(record, ["name", "display_name", "title", "key", "code", "transaction_id", "id"]),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (value) => (value ? <Tag color={statusColor(value)}>{String(value).toUpperCase()}</Tag> : "N/A"),
      },
      {
        title: "Details",
        key: "details",
        width: 420,
        render: (_, record) =>
          fields
            .filter((field) => !["name", "display_name", "title", "key", "code", "status"].includes(field))
            .slice(0, 4)
            .map((field) => (
              <div className="management-table__detail" key={field}>
                <strong>{labelFor(field)}:</strong> {displayRecordField(record, field)}
              </div>
            )),
      },
      {
        title: "Updated",
        dataIndex: "updated_at",
        key: "updated_at",
        width: 180,
        render: (value) => (value ? new Date(value).toLocaleString() : "N/A"),
      },
    ];

    return actionColumn ? [...baseColumns, actionColumn] : baseColumns;
  }, [activeResource, currentResource.readonly, deleteRecord, fields, openModal, statusToggleId, toggleGiftCardServiceStatus]);

  return (
    <ProgressLoader isLoading={loading || actionLoading}>
      <div className="Management">
        <SapaHeader page={pageTitle} />

        <Card className="management-shell">
        {!isDedicatedPage && (
          <Tabs
            activeKey={activeGroup}
            className="management-tabs"
            items={RESOURCE_GROUPS.map((group) => ({ key: group.key, label: group.label }))}
            onChange={setActiveGroup}
          />
        )}

        {!isDedicatedPage && (
          <div className="management-resource-row">
            <Select
              className="management-resource-select"
              value={activeResource}
              options={currentGroup.resources.map(({ key, label }) => ({ value: key, label }))}
              onChange={setActiveResource}
            />
          </div>
        )}

        <Filterbox
          displayFilter="none"
          searchAction={({ search_term }) => {
            setSearchTerm(search_term);
            fetchRecords(1, search_term);
          }}
          searchIsLoading={loading}
          showButtons
          showExportButton={false}
          showPrimaryAction={!currentResource.readonly}
          primaryActionLabel="New"
          onPrimaryAction={() => openModal()}
        />

        <div className="management-stats">
          <div>
            <Typography.Text type="secondary">Total</Typography.Text>
            <Typography.Title level={4}>{stats?.total || 0}</Typography.Title>
          </div>
          {Object.entries(stats?.by_status || {}).map(([status, total]) => (
            <div key={status}>
              <Typography.Text type="secondary">{status}</Typography.Text>
              <Typography.Title level={4}>{total}</Typography.Title>
            </div>
          ))}
        </div>

        {currentResource.readonly && (
          <Descriptions className="management-note" column={1} size="small">
            <Descriptions.Item label="Mode">Read only</Descriptions.Item>
          </Descriptions>
        )}

        {records.length > 0 || loading ? (
          <DataTable
            columns={columns}
            dataSource={records}
            loading={loading}
            rowKey="id"
            scrollX={1050}
          />
        ) : (
          <div className="management-empty">No records to display.</div>
        )}

        <SpPagination
          defaultCurrent={pagination?.current_page || 1}
          total={pagination?.total || 0}
          pageSize={pagination?.per_page || 15}
          onChange={fetchRecords}
        />
        </Card>

        <Modal
          className="management-edit-modal"
          destroyOnClose
          open={isModalOpen}
          title={`${editingRecord ? "Edit" : "New"} ${currentResource.label}`}
          okText="Save"
          confirmLoading={actionLoading}
          onCancel={() => setIsModalOpen(false)}
          onOk={submitForm}
        >
        <Form form={form} layout="vertical">
          {fields.map((field) => (
            <Form.Item
              key={field}
              label={labelFor(field)}
              name={field}
              rules={JSON_FIELDS.includes(field) ? [{ validator: (_, value) => {
                if (!value) return Promise.resolve();
                try {
                  JSON.parse(value);
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error("Enter valid JSON."));
                }
              } }] : []}
            >
              {BOOLEAN_FIELDS.includes(field) ? (
                <Select options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} />
              ) : field === "status" ? (
                <Select options={STATUS_OPTIONS.map((value) => ({ value, label: value }))} />
              ) : field === "operation" ? (
                <Select options={OPERATION_OPTIONS.map((value) => ({ value, label: value }))} />
              ) : field === "type" ? (
                <Select options={TYPE_OPTIONS.map((value) => ({ value, label: value }))} />
              ) : field === "denomination_type" ? (
                <Select options={DENOMINATION_TYPE_OPTIONS.map((value) => ({ value, label: value }))} />
              ) : JSON_FIELDS.includes(field) || ["description", "body", "message", "note", "review_remark"].includes(field) ? (
                <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
              ) : NUMBER_FIELDS.includes(field) ? (
                <InputNumber className="management-number-input" min={0} />
              ) : (
                <Input />
              )}
            </Form.Item>
          ))}
        </Form>
        </Modal>
      </div>
    </ProgressLoader>
  );
};

export default Management;
